import React, { useState, useRef } from 'react';

/**
 * HelpTooltip — subtle ⓘ icon with contextual help text.
 * Appears after 250ms hover delay (desktop), closes immediately on mouse leave.
 * Mobile: tap to toggle.
 * Optional learnMoreUrl prop adds a "Learn more →" link inside the tooltip.
 * Place next to a section/page title only.
 */
export function HelpTooltip({ text, learnMoreUrl }) {
  const [open, setOpen] = useState(false);
  const hoverTimer = useRef(null);

  const handleMouseEnter = () => {
    hoverTimer.current = setTimeout(() => setOpen(true), 250);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    setOpen(false);
  };

  return (
    <span
      className="relative inline-flex items-center ml-2 align-middle"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
      onBlur={() => setOpen(false)}
      onKeyDown={e => e.key === 'Enter' && setOpen(v => !v)}
      tabIndex={0}
      role="button"
      aria-label="Help"
      style={{ outline: 'none' }}
      data-testid="help-tooltip-icon"
    >
      <span className="w-[18px] h-[18px] rounded-full border border-slate-300 bg-white flex items-center justify-center text-[11px] text-slate-400 cursor-help select-none hover:border-orange-400 hover:text-orange-500 transition-colors leading-none">
        ⓘ
      </span>

      {open && (
        <span
          className="absolute left-6 top-1/2 -translate-y-1/2 z-50 w-64 bg-slate-900 text-white text-xs font-normal rounded-lg px-3 py-2.5 leading-relaxed shadow-xl pointer-events-none"
          style={{ fontFamily: 'inherit', whiteSpace: 'normal' }}
          data-testid="help-tooltip-text"
        >
          {text}
          {learnMoreUrl && (
            <a
              href={learnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-2 text-orange-400 hover:text-orange-300 pointer-events-auto"
              onClick={e => e.stopPropagation()}
              data-testid="help-tooltip-learn-more"
            >
              Learn more →
            </a>
          )}
          {/* Arrow pointing left toward the icon */}
          <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900" />
        </span>
      )}
    </span>
  );
}
